import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, BarChart2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { AppLogo } from '@/components/AppLogo';
import { Button } from '@/components/ui/button';
import { UserProfile } from '@/components/UserProfile';
import { getLogin, removeToken, removeLogin } from '@/lib/tokenStorage';

type ActiveTab = 'clients' | 'tariffs' | 'reports';

interface Props {
  activeTab: ActiveTab;
  onLogoClick?: () => void;
}

interface NavItem {
  tab: ActiveTab;
  label: string;
  path: string;
  icon?: LucideIcon;
}

const navItems: NavItem[] = [
  { tab: 'clients', label: 'Клиенты', path: '/clients' },
  { tab: 'tariffs', label: 'Тарифы', path: '/tariffs', icon: BookOpen },
  { tab: 'reports', label: 'Отчёты', path: '/reports', icon: BarChart2 },
];

export function PageHeader({ activeTab, onLogoClick }: Props) {
  const navigate = useNavigate();
  const userLogin = getLogin() ?? '';

  function handleLogout() {
    removeToken();
    removeLogin();
    navigate('/login');
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className='flex items-center justify-between flex-wrap gap-4 px-6 pt-6 pb-5'
    >
      <Button
        variant='ghost'
        onClick={onLogoClick ?? (() => navigate('/clients'))}
        className='hover:opacity-80 p-0 h-auto'
      >
        <AppLogo />
      </Button>
      <div className='flex gap-2'>
        {navItems.map(({ tab, label, path, icon: Icon }) => (
          <Button
            key={tab}
            variant='ghost'
            onClick={tab !== activeTab ? () => navigate(path) : undefined}
            className={
              tab === activeTab
                ? 'gap-2 bg-primary/10 border border-primary/30 text-primary font-medium'
                : 'gap-2 border border-border/50 bg-card text-foreground hover:bg-muted/50'
            }
          >
            {Icon && <Icon className='w-4 h-4' />}
            {label}
          </Button>
        ))}
        <UserProfile userLogin={userLogin} onLogout={handleLogout} />
      </div>
    </motion.div>
  );
}
